import { Core } from "@faire/design-tokens";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getSettingPhoneEmergencyStop } from "@faire/help-center-utils/settings/getSettingPhoneEmergencyStop";
import { getSettingPhoneSLA } from "@faire/help-center-utils/settings/getSettingPhoneSLA";
import { Phone } from "@faire/web--source/slate/icons/foundation/Phone";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterPhoneView } from "@faire/web-api--source/events/helpCenter/view/phone";
import React, { useEffect } from "react";

import { ContactUsOptionsWrapper } from "../../components/ContactUsOptionsWrapper";
import { ColumnWrapper, LeftMarginWrapper } from "../../components/EmailUs";
import { IconWrapper } from "../../components/IconWrapper";
import { OperatingTime } from "../../components/OperatingTime";
import { isPhoneInBusinessHours } from "../Phone/isPhoneInBusinessHours";

import { FormattedAvailableTime } from "./FormattedAvailableTime";
import { useFormatTime } from "./hooks/useFormatTime";
import { PhoneLanguageNotice } from "./PhoneLanguageNotice";

interface IPhoneProps {
  onClick?: () => void;
}

export const PhoneButton: React.FC<IPhoneProps> = ({ onClick }) => {
  const disablePhone = getSettingPhoneEmergencyStop();
  const handleClick = () => {
    onClick?.();
  };
  const textColor =
    isPhoneInBusinessHours() && !disablePhone
      ? Core.text.default
      : Core.text.subdued;

  const retailerToken = getRetailerToken() ?? "";
  const brandToken = getBrandToken() ?? "";

  useEffect(() => {
    trackHelpCenterPhoneView(retailerToken, brandToken);
  }, [brandToken, retailerToken]);

  const waitTime = getSettingPhoneSLA();
  const formattedWaitTime = useFormatTime(waitTime);

  if (disablePhone) {
    return (
      <ContactUsOptionsWrapper
        onClick={handleClick}
        disabled
        dataTestId="phone-disable-btn"
      >
        <ColumnWrapper>
          <IconWrapper disabled>
            <Phone fontSize="24" />
          </IconWrapper>
          <Spacer height="1x" />
          <Spacer height="1/2x" />
          <LeftMarginWrapper width="234px">
            <Typography variant="labelSansRegular" color={Core.text.subdued}>
              <StrictLocalMsg
                defaultMessage="Phone support is currently unavailable due to site maintenance."
                description={{
                  img: "https://cdn.faire.com/fastly/53a25a568b2932a465598f41f3fe71df6225228056b655af03e5d6d4838e5bf7.png",
                }}
              />
            </Typography>
          </LeftMarginWrapper>
        </ColumnWrapper>
      </ContactUsOptionsWrapper>
    );
  }
  return (
    <ContactUsOptionsWrapper
      onClick={handleClick}
      disabled={!isPhoneInBusinessHours()}
      dataTestId="phone-btn"
    >
      <ColumnWrapper>
        <IconWrapper disabled={!isPhoneInBusinessHours()}>
          <Phone fontSize="24" />
        </IconWrapper>
        <Spacer height="1x" />
        <Spacer height="1/2x" />
        <LeftMarginWrapper>
          <Typography variant="paragraphSansMedium" color={textColor}>
            <StrictLocalMsg
              defaultMessage="Request a call"
              description={{
                text: 'Title of the "Request a call" button',
                img: "https://cdn.faire.com/fastly/3e5042128a8728530ffecf6ba6eac69e1a5a49069a1441af7c25d0d1851ed8a5.png",
              }}
            />
          </Typography>
          <Spacer height="1x" />
          {isPhoneInBusinessHours() ? (
            <>
              <Typography color={textColor}>
                <StrictLocalMsg
                  defaultMessage="Average wait: {formattedWaitTime}"
                  description={{
                    text: "Average wait time for a call from Faire support",
                  }}
                  values={{ formattedWaitTime }}
                />
              </Typography>
              <PhoneLanguageNotice textColor={textColor} />
            </>
          ) : (
            <>
              <OperatingTime color={textColor} supportType="phone" />
              <FormattedAvailableTime supportType="phone" color={textColor} />
            </>
          )}
        </LeftMarginWrapper>
      </ColumnWrapper>
    </ContactUsOptionsWrapper>
  );
};
